import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { defineMessages } from 'react-intl.macro';

import { createStyles, ExpansionPanelDetails, Theme, withStyles, WithStyles } from '@material-ui/core';
import { CheckCircle as CheckCircleIcon } from '@material-ui/icons';
import classnames from 'classnames';
import { concat, filter, includes, indexOf, isEmpty, values, without } from 'lodash';
import { Patient, usePatientMerge } from 'requests/resources';

import { useCurrentUser } from '@lib/hooks';
import { UserBase } from '@lib/resources/types';

import { WrapperCardPatient } from 'deprecated/query-wrappers';

import DialogHeader from 'components/DialogHeader';
import PatientsTable, { callbackContact, sortContact } from 'components/PatientsTable';

import Button from './Button';
import Dialog, { DialogProps } from './Dialog';
import DialogActions from './DialogActions';
import DialogContent from './DialogContent';
import ExpansionPanel from './ExpansionPanel';
import StepperPatientMerge, { StepperPatientMergeStep } from './StepperPatientMerge';
import SvgIconWrapper from './SvgIconWrapper';
import TextField from './TextField';
import Typography from './Typography';
import TypographyWithIcon from './TypographyWithIcon';

const styles = ({
  spacing: { unit },
  custom: {
    palette: { colorLightBlue },
  },
}: Theme) =>
  createStyles({
    root: {},
    dialogContent: {
      paddingTop: unit,
      paddingBottom: unit * 5,
    },
    expansionPanel: {
      marginBottom: unit * 3,
    },
    expansionPanelDetails: {
      padding: 0,
      border: `1px solid ${colorLightBlue}`,
    },
    cardPatient: {
      width: '100%',
    },
  });

const messages = defineMessages({
  title: {
    id: 'components.v2.DialogPatientMerge.title',
    defaultMessage: 'Review & Merge Records',
  },
  subheaderOriginal: {
    id: 'components.v2.DialogPatientMerge.subheaderOriginal',
    defaultMessage: 'CURRENT PATIENT ENTRY',
  },
  subheaderDups: {
    id: 'components.v2.DialogPatientMerge.subheaderDups',
    defaultMessage: 'IDENTIFIED DUPLICATES',
  },
  helperTextMarkDuplicates: {
    id: 'components.v2.DialogPatientMerge.helperTextMarkDuplicates',
    defaultMessage: 'Mark which records are duplicates of the original patient entry:',
  },
  helperTextDone: {
    id: 'components.v2.DialogPatientMerge.helperTextDone',
    defaultMessage:
      '{count, plural, one {Successfully deleted and merged # duplicate entry} other {Successfully deleted and merged # duplicate entries}}',
  },
  helperTextReview: {
    id: 'components.v2.DialogPatientMerge.helperTextReview',
    defaultMessage:
      'Would you like to merge and delete these duplicate entries? Please double check your selections and <b>type the email associated with your account</b> to confirm.',
  },
  columnName: {
    id: 'components.v2.DialogPatientMerge.columnName',
    defaultMessage: 'NAME',
  },
  columnExternalId: {
    id: 'components.v2.DialogPatientMerge.columnExternalId',
    defaultMessage: 'EHR ID',
  },
  columnDateOfBirth: {
    id: 'components.v2.DialogPatientMerge.columnDateOfBirth',
    defaultMessage: 'DOB',
  },
  columnContact: {
    id: 'components.v2.DialogPatientMerge.columnContact',
    defaultMessage: 'CONTACT',
  },
  email: {
    id: 'components.v2.DialogPatientMerge.email',
    defaultMessage: 'Email',
  },
  placeholderEmail: {
    id: 'components.v2.DialogPatientMerge.placeholderEmail',
    defaultMessage: 'Email address',
  },
  errorEmailMatch: {
    id: 'components.v2.DialogPatientMerge.errorEmailMatch',
    defaultMessage: `Email doesn't match`,
  },
  actionCancel: {
    id: 'components.v2.DialogPatientMerge.actionCancel',
    defaultMessage: 'Cancel',
  },
  actionNext: {
    id: 'components.v2.DialogPatientMerge.actionNext',
    defaultMessage: 'Next',
  },
  actionSubmit: {
    id: 'components.v2.DialogPatientMerge.actionSubmit',
    defaultMessage: 'Delete and Merge',
  },
  actionClose: {
    id: 'components.v2.DialogPatientMerge.actionClose',
    defaultMessage: 'Close',
  },
});

export interface DialogPatientMergeProps extends Omit<DialogProps, 'classes'>, WithStyles<typeof styles> {
  patient: string;
  patientDups?: Partial<Patient>[];
}

const DialogPatientMerge: React.FC<DialogPatientMergeProps> = ({
  classes,
  className,
  patient,
  patientDups = [],
  onClose,
  ...rest
}) => {
  const { formatMessage } = useIntl();
  const { email: currentUserEmail } = useCurrentUser()!;
  const patientMergeMutation = usePatientMerge();

  const [step, setStep] = useState(StepperPatientMergeStep.MarkDuplicates);
  const [patientsSelected, setPatientsSelected] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [email, setEmail] = useState('');
  const isValidEmail = currentUserEmail === email;

  // Reset state on close
  useEffect(() => {
    if (!rest.open) {
      setStep(StepperPatientMergeStep.MarkDuplicates);
      setPatientsSelected([]);
      setSubmitting(false);
      setEmail('');
    }
  }, [rest.open]);

  const handleSubmit = () => {
    setSubmitting(true);
    patientMergeMutation.mutate(
      { idToKeep: patient, idsToTrash: patientsSelected, source: 'ui' },
      { onSuccess: () => setStep(StepperPatientMergeStep.Done) },
    );
  };

  return (
    <Dialog className={classnames(classes.root, className)} maxWidth="lg" fullWidth onClose={onClose} {...rest}>
      <DialogHeader onRequestClose={onClose} title={formatMessage(messages.title)} />
      <StepperPatientMerge activeStep={indexOf(values(StepperPatientMergeStep), step)} onStepClick={setStep} />
      <DialogContent className={classes.dialogContent}>
        {/* Patient */}
        <ExpansionPanel
          className={classes.expansionPanel}
          color="supportPowder"
          summary={formatMessage(messages.subheaderOriginal)}
          elevation={0}
          defaultExpanded
        >
          <ExpansionPanelDetails className={classes.expansionPanelDetails}>
            <WrapperCardPatient className={classes.cardPatient} patient={patient} elevation={0} />
          </ExpansionPanelDetails>
        </ExpansionPanel>
        {/* Helper text */}
        {step === StepperPatientMergeStep.MarkDuplicates && (
          <Typography variant="bW400F15" paragraph>
            {formatMessage(messages.helperTextMarkDuplicates)}
          </Typography>
        )}
        {step === StepperPatientMergeStep.Done && (
          <TypographyWithIcon
            icon={<SvgIconWrapper Icon={CheckCircleIcon} color="success" fontSize="inherit" />}
            variant="h3W600F15"
            paragraph
          >
            {formatMessage(messages.helperTextDone, { count: patientsSelected.length })}
          </TypographyWithIcon>
        )}
        {/* Duplicates */}
        {step !== StepperPatientMergeStep.Done && (
          <ExpansionPanel
            className={classes.expansionPanel}
            color="supportPowder"
            summary={formatMessage(messages.subheaderDups)}
            elevation={0}
            defaultExpanded
          >
            <ExpansionPanelDetails className={classes.expansionPanelDetails}>
              <PatientsTable
                columnData={[
                  {
                    id: 'name',
                    label: formatMessage(messages.columnName),
                    callback: ({ firstname, lastname }: UserBase) => `${lastname}, ${firstname}`,
                  },
                  {
                    id: 'dateOfBirth',
                    label: formatMessage(messages.columnDateOfBirth),
                    date: 'll',
                    style: { width: 200 },
                  },
                  { id: 'externalId.value', label: formatMessage(messages.columnExternalId) },
                  {
                    id: 'contact',
                    label: formatMessage(messages.columnContact),
                    callback: callbackContact,
                    sort: sortContact,
                  },
                ]}
                patients={
                  step === StepperPatientMergeStep.MarkDuplicates
                    ? patientDups
                    : filter(patientDups, ({ _id }) => includes(patientsSelected, _id))
                }
                patientsSelected={patientsSelected}
                selectable={step === StepperPatientMergeStep.MarkDuplicates}
                onCheck={({ _id }, checked) =>
                  setPatientsSelected((prevPatientsSelected) =>
                    checked ? concat(prevPatientsSelected, _id) : without(prevPatientsSelected, _id),
                  )
                }
                order="asc"
                orderBy="name"
                hideActions
              />
            </ExpansionPanelDetails>
          </ExpansionPanel>
        )}
        {/* Review */}
        {step === StepperPatientMergeStep.Review && (
          <>
            <Typography
              variant="bW400F15"
              paragraph
              dangerouslySetInnerHTML={{ __html: formatMessage(messages.helperTextReview) }}
            />
            <TextField
              label={formatMessage(messages.email)}
              placeholder={formatMessage(messages.placeholderEmail)}
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{
                'data-lh-id': 'dialog-patient-merge-email',
              }}
              fullWidth
              required
              autoFocus
              helperText={!!email && !isValidEmail && formatMessage(messages.errorEmailMatch)}
              error={!!email && !isValidEmail}
            />
          </>
        )}
      </DialogContent>
      {/* Actions */}
      <DialogActions>
        {step !== StepperPatientMergeStep.Done && (
          <Button color="primary" rounded data-lh-id="dialog-patient-merge-close-button" onClick={onClose}>
            {formatMessage(messages.actionCancel)}
          </Button>
        )}
        {step === StepperPatientMergeStep.MarkDuplicates && (
          <Button
            variant="contained"
            color="primary"
            rounded
            onClick={() => setStep(StepperPatientMergeStep.Review)}
            disabled={isEmpty(patientsSelected)}
            data-lh-id="dialog-patient-merge-next-button"
          >
            {formatMessage(messages.actionNext)}
          </Button>
        )}
        {step === StepperPatientMergeStep.Review && (
          <Button
            variant="contained"
            color="secondary"
            rounded
            onClick={handleSubmit}
            disabled={!isValidEmail || submitting}
            data-lh-id="dialog-patient-merge-submit-button"
          >
            {formatMessage(messages.actionSubmit)}
          </Button>
        )}
        {step === StepperPatientMergeStep.Done && (
          <Button
            data-lh-id="dialog-patient-merge-close-button"
            variant="contained"
            color="primary"
            rounded
            onClick={onClose}
          >
            {formatMessage(messages.actionClose)}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default withStyles(styles, { name: 'LumaDialogPatientMerge' })(DialogPatientMerge);
